import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";

import { withNavigation } from "../../../utils/wrapper";
import { loading } from "../../../actions/page";
import { getDivorce, saveDivorce } from "../../../actions/matter.divorce";

import MarriageInfo from "./marriage";
import Party from "./party";
import Children from "./children";

const normalizeChild = (child = {}) => ({
  ...(child || {}),
  Person: {
    ...(child?.Person || {}),
  },
});

const normalizeParty = (party = {}) => ({
  ...(party || {}),
  Person: {
    ...(party?.Person || {}),
  },
  EmployerAddress: {
    ...(party?.EmployerAddress || {}),
  },
});

const normalizeDivorceFromApi = (item = {}, uuid) => ({
  ...(item || {}),
  uuid: item?.uuid ?? uuid,
  Plaintiff: normalizeParty(item?.Plaintiff || {}),
  Defendant: normalizeParty(item?.Defendant || {}),
  Children: Array.isArray(item?.Children)
    ? item.Children.map(normalizeChild)
    : Array.isArray(item?.children)
      ? item.children.map(normalizeChild)
      : [],
});

const toDigits = (value) => String(value ?? "").replace(/\D/g, "");

const normalizePhone = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");

  if (digits.length === 10) return `1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return digits;

  return String(value ?? "");
};

const toPhoneInputValue = (value) => {
  const normalized = normalizePhone(value);
  if (/^\d{11}$/.test(normalized)) return `+${normalized}`;
  return normalized;
};

const toIsoOrRaw = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return value;
};

const isPlainObject = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  !(value instanceof Date);

const isBlank = (value) => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
};

const mergeBlankOnly = (target, source) => {
  if (source == null) return target;
  if (target == null) return source;

  if (Array.isArray(source)) {
    return isBlank(target) ? source : target;
  }

  if (isPlainObject(source)) {
    const result = { ...(target || {}) };
    for (const key of Object.keys(source)) {
      const targetValue = result[key];
      const sourceValue = source[key];

      if (isPlainObject(sourceValue)) {
        result[key] = mergeBlankOnly(targetValue || {}, sourceValue);
      } else if (Array.isArray(sourceValue)) {
        result[key] = isBlank(targetValue) ? sourceValue : targetValue;
      } else if (isBlank(targetValue) && !isBlank(sourceValue)) {
        result[key] = sourceValue;
      }
    }
    return result;
  }

  return isBlank(target) ? source : target;
};



const mapGender = (value) => {
  const v = String(value ?? "").trim().toUpperCase();
  if (v === "M" || v === "MALE") return "male";
  if (v === "F" || v === "FEMALE") return "female";
  if (v === "O" || v === "OTHER") return "other";
  return "unknown";
};

const mapCountry = (section = {}) => {
  return section?.Country || section?.country || section?.options || "";
};

const mapAddress = (section = {}) => ({
  line1: section?.line1 ?? "",
  line2: section?.line2 ?? "",
  city: section?.city ?? "",
  county: section?.county ?? "",
  state: section?.state ?? section?.State ?? "",
  country: section?.country ?? section?.Country ?? section?.options ?? "",
  zipCode: String(section?.zipCode ?? "").replace(/_/g, ""),
});

const mapBirthplace = (section = {}) => ({
  state: section?.State ?? section?.state ?? "",
  country: mapCountry(section),
});

const mapTitleFromSurvey = (section = {}) => {
  const gender = String(section?.Gender ?? "").trim().toUpperCase();
  if (gender === "M") return "husband";
  if (gender === "F") return "wife";
  return "spouse";
};

const mapChildFromSurvey = (section = {}) => ({
  legalCustody: "joint",
  physicalCustody: "joint",
  Person: {
    firstName: section?.Name?.first ?? "",
    middleName: section?.Name?.middle ?? "",
    lastName: section?.Name?.last ?? "",
    generation: section?.Name?.generation ?? "",
    ssn: toDigits(section?.Ssn?.Number).slice(0, 9),
    birthDate: toIsoOrRaw(section?.Birthplace?.Date),
    gender: mapGender(section?.Gender),
    Birthplace: mapBirthplace(section?.Birthplace || {}),
  },
});

const mapPartyFromSurvey = (section = {}) => {
  const pregnancyOptions = String(section?.Pregnancy?.options ?? "0");
  const pregnant = pregnancyOptions === "1" || pregnancyOptions.toLowerCase?.() === "true";

  return {
    title: mapTitleFromSurvey(section),
    maiden: section?.Maiden ?? "",
    restore: false,
    pregnant,
    dueDate: pregnant ? toIsoOrRaw(section?.Pregnancy?.Due) : null,
    marriages: Number(section?.Marriages ?? 1),
    education: Number(section?.Education ?? 0),
    military: "unknown",
    employerName: section?.EmployerName ?? "",
    employerPhone: toPhoneInputValue(section?.EmployerPhone),
    driversLicenseNumber: section?.License?.Number,
    driversLicenseState: section?.License?.Issuer,
    Person: {
      firstName: section?.Name?.first ?? "",
      middleName: section?.Name?.middle ?? "",
      lastName: section?.Name?.last ?? "",
      generation: "",
      email: section?.Email ?? "",
      phone: toPhoneInputValue(section?.Phone),
      ssn: toDigits(section?.Ssn?.Number).slice(0, 9),
      birthDate: toIsoOrRaw(section?.Birthplace?.Date),
      gender: mapGender(section?.Gender),
      Residence: mapAddress(section?.Address || {}),
      Birthplace: mapBirthplace(section?.Birthplace || {}),
    },
    EmployerAddress: mapAddress(section?.EmployerAddress || {}),
  };
};

const mapMarriageFromSurvey = (section = {}) => ({
  Location: mapAddress(section?.Location || {}),
  LastCohabitation: mapAddress(section?.Cohabitation || {}),
  marriageDate: toIsoOrRaw(section?.MarriageDates?.marriageDate),
  separationDate: toIsoOrRaw(section?.MarriageDates?.separationDate),
  livingTogether: false,
  intentDate: null,
  intentCode: "plaintiff",
  agreementDate: null,
});

const mapSurveyImportToDivorce = (surveyImport) => {
  const data = surveyImport?.data || {};
  const client = data?.Client || {};
  const spouse = data?.Spouse || {};
  const marriage = data?.Marriage || {};
  const children = Array.isArray(data?.Children?.Children)
    ? data.Children.Children
    : [];

  return {
    Plaintiff: mapPartyFromSurvey(client),
    Defendant: mapPartyFromSurvey(spouse),
    ...mapMarriageFromSurvey(marriage),
    Children: children.map(mapChildFromSurvey),
  };
};

const getByPath = (obj, path) => {
  if (!path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const setByPath = (obj, path, value) => {
  const parts = String(path).split(".");
  const result = { ...(obj || {}) };
  let current = result;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    current[key] = isPlainObject(current[key]) ? { ...current[key] } : {};
    current = current[key];
  }

  current[parts[parts.length - 1]] = value;
  return result;
};

const normalizeComparable = (value) => {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).trim();
};

const flattenPartySuggestions = (party = {}) => ({
    title: party?.title,
    maiden: party?.maiden,
    restore: party?.restore,
    pregnant: party?.pregnant,
    dueDate: party?.dueDate,
    marriages: party?.marriages,
    education: party?.education,
    military: party?.military,
    employerName: party?.employerName,
    employerPhone: party?.employerPhone,
    driversLicenseNumber: party?.driversLicenseNumber,
    driversLicenseState: party?.driversLicenseState,

    Person: {
        firstName: party?.Person?.firstName,
        middleName: party?.Person?.middleName,
        lastName: party?.Person?.lastName,
        generation: party?.Person?.generation,
        email: party?.Person?.email,
        phone: party?.Person?.phone,
        ssn: party?.Person?.ssn,
        birthDate: party?.Person?.birthDate,
        gender: party?.Person?.gender,
        Birthplace: party?.Person?.Birthplace || {},
        Residence: party?.Person?.Residence || {},
    },

    EmployerAddress: party?.EmployerAddress || {},
});

const flattenMarriageSuggestions = (divorce = {}) => ({
    marriageDate: divorce?.marriageDate,
    separationDate: divorce?.separationDate,
    livingTogether: divorce?.livingTogether,
    intentDate: divorce?.intentDate,
    intentCode: divorce?.intentCode,
    agreementDate: divorce?.agreementDate,
    Location: divorce?.Location || {},
    LastCohabitation: divorce?.LastCohabitation || {},
});

const flattenChildSuggestions = (child = {}) => ({
    uuid: child?.uuid,
    _cid: child?._cid,
    legalCustody: child?.legalCustody,
    physicalCustody: child?.physicalCustody,
    Person: {
        firstName: child?.Person?.firstName,
        middleName: child?.Person?.middleName,
        lastName: child?.Person?.lastName,
        generation: child?.Person?.generation,
        ssn: child?.Person?.ssn,
        birthDate: child?.Person?.birthDate,
        gender: child?.Person?.gender,
        Birthplace: child?.Person?.Birthplace || {},
    },
});

const Divorce = forwardRef(function Divorce(props, ref) {
    const {
        auth,
        uuid,
        surveyImport,
        loading,
        getDivorce,
        saveDivorce,
    } = props;

    const loadingDataRef = useRef(false);
    const appliedImportKeyRef = useRef(null);

    const [divorce, setDivorce] = useState({ uuid });
    const [changed, setChanged] = useState(false);
    const [rejectedSuggestions, setRejectedSuggestions] = useState({});
    const [modal, setModal] = useState({
        error: { show: false, title: "", body: "" },
        confirm: { show: false, title: "", body: "", onOk: null },
        report: { show: false },
    });

    const onErrorShow = useCallback((config = {}) => {
        setModal((prev) => ({
            ...prev,
            error: {
                ...prev.error,
                ...config,
                show: true,
            },
        }));
    }, []);

    const markChangedMerge = useCallback((patch) => {
        setDivorce((prev) => ({
            ...(prev || {}),
            ...(patch || {}),
        }));
        setChanged(true);
    }, []);

    const mappedSurveyDivorce = useMemo(() => {
        return surveyImport?.data ? mapSurveyImportToDivorce(surveyImport) : null;
    }, [surveyImport]);

    const applySurveyImport = useCallback((baseDivorce = {}) => {
        if (!surveyImport?.data) return baseDivorce;

        const mapped = mapSurveyImportToDivorce(surveyImport);
        return mergeBlankOnly(baseDivorce || {}, mapped);
    }, [surveyImport]);

    const makeChildSuggestions = useCallback(() => {
        const mappedChildren = Array.isArray(mappedSurveyDivorce?.Children)
            ? mappedSurveyDivorce.Children
            : [];
        const currentChildren = Array.isArray(divorce?.Children)
            ? divorce.Children
            : [];

        return mappedChildren.map((mappedChild, index) => {
            const flat = flattenChildSuggestions(mappedChild || {});
            const currentChild = currentChildren[index] || {};

            return Object.keys(flat).reduce((acc, key) => {
                const suggestionKey = `Child.${index}.${key}`;
                const incoming = flat[key];
                const current = getByPath(currentChild, key);

                if (incoming === undefined) return acc;
                if (rejectedSuggestions[suggestionKey]) return acc;
                if (normalizeComparable(current) === normalizeComparable(incoming)) return acc;

                acc[key] = incoming;
                return acc;
            }, {});
        });
    }, [mappedSurveyDivorce, divorce, rejectedSuggestions]);

    const childSuggestions = useMemo(
        () => makeChildSuggestions(),
        [makeChildSuggestions]
    );

    const applyChildSuggestion = useCallback((index, path, value) => {
        setDivorce((prev) => {
            const nextChildren = Array.isArray(prev?.Children) ? [...prev.Children] : [];
            const currentChild = nextChildren[index] || {};
            nextChildren[index] = setByPath(currentChild, path, value);

            return {
                ...(prev || {}),
                Children: nextChildren,
            };
        });

        setRejectedSuggestions((prev) => {
            const next = { ...(prev || {}) };
            delete next[`Child.${index}.${path}`];
            return next;
        });

        setChanged(true);
    }, []);

    const rejectChildSuggestion = useCallback((index, path) => {
        setRejectedSuggestions((prev) => ({
            ...(prev || {}),
            [`Child.${index}.${path}`]: true,
        }));
    }, []);

    
    const makeMarriageSuggestions = useCallback(() => {
        const flat = flattenMarriageSuggestions(mappedSurveyDivorce || {});
        const currentMarriage = divorce || {};

        return Object.keys(flat).reduce((acc, key) => {
            const suggestionKey = `Marriage.${key}`;
            const incoming = flat[key];
            const current = currentMarriage?.[key];

            if (incoming === undefined) return acc;
            if (rejectedSuggestions[suggestionKey]) return acc;
            if (normalizeComparable(current) === normalizeComparable(incoming)) return acc;

            acc[key] = incoming;
            return acc;
        }, {});
    }, [mappedSurveyDivorce, divorce, rejectedSuggestions]);

    const marriageSuggestions = useMemo(
        () => makeMarriageSuggestions(),
        [makeMarriageSuggestions]
    );

    const applyMarriageSuggestion = useCallback((path, value) => {
        setDivorce((prev) => setByPath(prev || {}, path, value));

        setRejectedSuggestions((prev) => {
            const next = { ...(prev || {}) };
            delete next[`Marriage.${path}`];
            return next;
        });

        setChanged(true);
    }, []);

    const rejectMarriageSuggestion = useCallback((path) => {
        setRejectedSuggestions((prev) => ({
            ...(prev || {}),
            [`Marriage.${path}`]: true,
        }));
    }, []);

    const loadData = useCallback(async ({ showLoader = true } = {}) => {
        if (!uuid || loadingDataRef.current) return;

        loadingDataRef.current = true;
        if (showLoader) loading(true);

        try {
            const divorceResp = await getDivorce({ uuid });
            const item = divorceResp?.item ?? {};
            const normalized = normalizeDivorceFromApi(item, uuid);
            const merged = applySurveyImport(normalized);

            setDivorce(merged);
            setChanged(false);

            if (surveyImport?.key || surveyImport?.title || surveyImport?.matterUuid || surveyImport?.mode) {
                appliedImportKeyRef.current =
                    surveyImport?.key ||
                    `${surveyImport?.title || ""}:${surveyImport?.matterUuid || ""}:${surveyImport?.mode || ""}`;
            }
        } catch (err) {
            onErrorShow({
                title: "Failed to load divorce",
                body: err?.message || "An unexpected error occurred while loading.",
            });
        } finally {
            if (showLoader) loading(false);
            loadingDataRef.current = false;
        }
    }, [uuid, loading, getDivorce, onErrorShow, applySurveyImport, surveyImport]);

    useEffect(() => {
        setDivorce({ uuid });
        setChanged(false);
        setRejectedSuggestions({});
        appliedImportKeyRef.current = null;
    }, [uuid]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (!surveyImport?.data) return;
        if (loadingDataRef.current) return;

        const nextKey =
            surveyImport?.key ||
            `${surveyImport?.title || ""}:${surveyImport?.matterUuid || ""}:${surveyImport?.mode || ""}`;

        if (appliedImportKeyRef.current === nextKey) return;

        setDivorce((prev) => applySurveyImport(prev || { uuid }));
        appliedImportKeyRef.current = nextKey;
    }, [surveyImport, applySurveyImport, uuid]);

    const saveChanges = useCallback(
        async (e) => {
            e?.preventDefault?.();
            loading(true);

            try {
                await saveDivorce({
                    ...(divorce || {}),
                    uuid,
                });

                await loadData({ showLoader: false });
                setChanged(false);
            } catch (err) {
                onErrorShow({
                    title: "Save failed",
                    body: err?.message || "An unexpected error occurred while saving.",
                });
            } finally {
                loading(false);
            }
        },
        [loading, saveDivorce, divorce, uuid, loadData, onErrorShow]
    );

    const onPlaintiffChange = useCallback((payload) => {
        const values = payload?.values ?? payload ?? {};
        setDivorce((prev) => ({
            ...(prev || {}),
            Plaintiff: {
                ...(prev?.Plaintiff || {}),
                ...(values || {}),
                Person: {
                    ...(prev?.Plaintiff?.Person || {}),
                    ...(values?.Person || {}),
                },
                EmployerAddress: {
                    ...(prev?.Plaintiff?.EmployerAddress || {}),
                    ...(values?.EmployerAddress || {}),
                },
            },
        }));
        setChanged(true);
    }, []);

    const onDefendantChange = useCallback((payload) => {
        const values = payload?.values ?? payload ?? {};
        setDivorce((prev) => ({
            ...(prev || {}),
            Defendant: {
                ...(prev?.Defendant || {}),
                ...(values || {}),
                Person: {
                    ...(prev?.Defendant?.Person || {}),
                    ...(values?.Person || {}),
                },
                EmployerAddress: {
                    ...(prev?.Defendant?.EmployerAddress || {}),
                    ...(values?.EmployerAddress || {}),
                },
            },
        }));
        setChanged(true);
    }, []);

    const onMarriageChange = useCallback((payload) => {
        markChangedMerge(payload || {});
    }, [markChangedMerge]);

    const onChildrenChange = useCallback((payload) => {
        const nextChildren = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.Children)
                ? payload.Children
                : [];

        markChangedMerge({
            Children: nextChildren,
        });
    }, [markChangedMerge]);

    const makePartySuggestions = useCallback((partyKey) => {
        const mappedParty = mappedSurveyDivorce?.[partyKey] || {};
        const flat = flattenPartySuggestions(mappedParty);
        const currentParty = divorce?.[partyKey] || {};

        return Object.keys(flat).reduce((acc, key) => {
            const suggestionKey = `${partyKey}.${key}`;
            const incoming = flat[key];
            const current = getByPath(currentParty, key);

            if (incoming === undefined) return acc;
            if (rejectedSuggestions[suggestionKey]) return acc;
            if (key === "education" && Number(incoming) === 0) return acc;
            if (normalizeComparable(current) === normalizeComparable(incoming)) return acc;

            acc[key] = incoming;
            return acc;
        }, {});
    }, [mappedSurveyDivorce, divorce, rejectedSuggestions]);

    const plaintiffSuggestions = useMemo(
        () => makePartySuggestions("Plaintiff"),
        [makePartySuggestions]
    );

    const defendantSuggestions = useMemo(
        () => makePartySuggestions("Defendant"),
        [makePartySuggestions]
    );

    const applyPartySuggestion = useCallback((partyKey, path, value) => {
        setDivorce((prev) => {
            const currentParty = prev?.[partyKey] || {};
            const nextParty = setByPath(currentParty, path, value);

            return {
                ...(prev || {}),
                [partyKey]: nextParty,
            };
        });

        setRejectedSuggestions((prev) => {
            const next = { ...(prev || {}) };
            delete next[`${partyKey}.${path}`];
            return next;
        });

        setChanged(true);
    }, []);

    const rejectPartySuggestion = useCallback((partyKey, path) => {
        setRejectedSuggestions((prev) => ({
            ...(prev || {}),
            [`${partyKey}.${path}`]: true,
        }));
    }, []);

    const validate = useCallback(() => {
        return { ok: true };
    }, []);

    const getData = useCallback(() => {
        return { divorce };
    }, [divorce]);

    useImperativeHandle(
        ref,
        () => ({
            saveChanges,
            validate,
            getData,
            changed,
        }),
        [saveChanges, validate, getData, changed]
    );

    return (
        <>
            <Card body className="mb-5">
                <Party
                    caseUuid={uuid}
                    caption="Plaintiff"
                    {...(divorce?.Plaintiff || {})}
                    suggestions={plaintiffSuggestions}
                    onApplySuggestion={(path, value) => applyPartySuggestion("Plaintiff", path, value)}
                    onRejectSuggestion={(path) => rejectPartySuggestion("Plaintiff", path)}
                    onChange={onPlaintiffChange}
                />
            </Card>

            <Card body className="mb-5">
                <Party
                    caseUuid={uuid}
                    caption="Defendant"
                    {...(divorce?.Defendant || {})}
                    suggestions={defendantSuggestions}
                    onApplySuggestion={(path, value) => applyPartySuggestion("Defendant", path, value)}
                    onRejectSuggestion={(path) => rejectPartySuggestion("Defendant", path)}
                    onChange={onDefendantChange}
                />
            </Card>

            <Card body className="mb-5">
                <MarriageInfo
                    {...(divorce || {})}
                    suggestions={marriageSuggestions}
                    onApplySuggestion={(path, value) => applyMarriageSuggestion(path, value)}
                    onRejectSuggestion={(path) => rejectMarriageSuggestion(path)}
                    onChange={onMarriageChange}
                />
            </Card>

            <Card body className="mb-5">
                <Children
                    caseUuid={uuid}
                    children={divorce?.Children || []}
                    suggestions={childSuggestions}
                    onApplySuggestion={(index, path, value) => applyChildSuggestion(index, path, value)}
                    onRejectSuggestion={(index, path) => rejectChildSuggestion(index, path)}
                    onChange={onChildrenChange}
                />
            </Card>
        </>
    );
});

Divorce.propTypes = {
    auth: PropTypes.object.isRequired,
    uuid: PropTypes.string,
    surveyImport: PropTypes.object,
    loading: PropTypes.func.isRequired,
    getDivorce: PropTypes.func.isRequired,
    saveDivorce: PropTypes.func.isRequired,
};

Divorce.defaultProps = {
    uuid: null,
    surveyImport: null,
};

const mapStateToProps = (state, ownProps) => ({
    auth: state.auth,
    uuid: ownProps?.uuid || ownProps?.params?.uuid || null,
    surveyImport: ownProps?.surveyImport || null,
});

export default withNavigation(
    connect(
        mapStateToProps,
        {
            loading,
            getDivorce,
            saveDivorce,
        },
        null,
        { forwardRef: true }
    )(Divorce)
);